<template>
    <div
        class="ppv2-flex ppv2-flex-wrap ppv2-w-full ppv2-step-wrapper md:ppv2-gap-[3.125vw]"
        :style="{ paddingLeft: normalizeSize(55) }"
    >
        <!-- Form Section -->
        <div class="form-section">
            <HeaderStep :step="step" :sizes="sizes" />

            <!-- Empty State -->
            <div v-if="comboItems.length === 0" class="empty-state ppv2-mb-6">
                <DashedCard
                    :marginTop="37"
                    :marginBottom="37"
                    min-height="150px"
                    :custom-style="{ padding: normalizeSize(24, true) }"
                >
                    <template #content>
                        <div
                            class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-items-center ppv2-justify-center"
                            :style="{ gap: normalizeSizeVertical(14, true) }"
                        >
                            <p class="!ppv2-mb-0 ppv2-text-black" style="font-size: 12px">
                                {{ $t('package.step3.emptyState.message') }}
                            </p>
                            <BaseButton type="button" variant="secondary" :customFontSize="12" @click="addNewItem">
                                <span class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                    <Plus :size="12" />
                                    {{ $t('package.step3.emptyState.button') }}
                                </span>
                            </BaseButton>
                        </div>
                    </template>
                </DashedCard>
            </div>

            <!-- Combo Items List -->
            <div v-else class="ppv2-mb-6">
                <!-- Same Time Option -->
                <div class="ppv2-mb-6">
                    <!-- $t('package.step3.sameTimeQuestion') ||  -->
                    <SelectableList
                        :label="$t('package.step3.sameTimeQuestion')"
                        :value="parameters.is_same_time"
                        @input="onSameTimeChange"
                        :options="sameTimeOptions"
                    />
                </div>

                <!-- Participants différents -->
                <div v-if="shouldShowMultiParticipants" class="ppv2-mb-6" :style="{ marginTop: normalizeSizeVertical(30, true) }">
                    <SelectableList
                        :label="$t('package.step5.differentParticipants.question')"
                        :value="parameters.is_multi_participants"
                        @input="(val) => updateParameterWithSideEffects({ key: 'is_multi_participants', value: val })"
                        :options="multiParticipantsOptions"
                    />
                </div>

                <!-- Combo Items List -->
                <div class="combo-items-list">
                    <DashedCard
                        :title="$t('package.step3.title')"
                        :title-size="15"
                        :marginTop="37"
                        :marginBottom="37"
                        min-height="150px"
                        :custom-style="{ padding: normalizeSize(24, true) }"
                    >
                        <template #content>
                            <div class="ppv2-flex-1 ppv2-flex ppv2-flex-col ppv2-w-full">
                                <div class="ppv2-space-y-4 ppv2-mb-6 ppv2-w-full">
                                    <div
                                        v-for="(item, index) in comboItems"
                                        :key="index"
                                        class="combo-item-container ppv2-flex ppv2-items-center ppv2-relative"
                                        draggable="true"
                                        @dragstart="onDragStart($event, index)"
                                        @dragover="onDragOver($event)"
                                        @drop="onDrop($event, index)"
                                        @dragenter="onDragEnter($event)"
                                        @dragleave="onDragLeave($event)"
                                        @dragend="onDragEnd($event)"
                                    >
                                        <GripVertical
                                            :size="16"
                                            color="#9D96A8"
                                            class="ppv2-cursor-grab ppv2-absolute ppv2-left-[-17px]"
                                        />
                                        <div class="combo-item ppv2-border ppv2-rounded-lg ppv2-p-4 ppv2-flex-1">
                                            <div
                                                class="ppv2-flex ppv2-flex-wrap ppv2-items-start ppv2-w-full"
                                                :style="{ gap: normalizeSize(16, true) }"
                                            >
                                                <!-- Activity Selection -->
                                                <div class="ppv2-flex-1">
                                                    <BaseSelect
                                                        :label="$t('package.step3.choiceLabel') + ' ' + (index + 1)"
                                                        v-model="item.selectedActivity"
                                                        :options="getAvailableActivities(index)"
                                                        optionLabel="name"
                                                        compact
                                                        :reduce="(opt) => opt"
                                                        :placeholder="$t('package.step3.activityLabel')"
                                                        :searchable="true"
                                                        :customSizeLabel="14"
                                                        @input="onActivityChange(index, $event)"
                                                        class="transparent-select ppv2-w-full"
                                                        class-added="!ppv2-rounded-[4px] !ppv2-h-[30px]"
                                                        :floatingLabel="true"
                                                        :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#f3f4f5'"
                                                    />
                                                </div>

                                                <!-- Duration Selection -->
                                                <div class="ppv2-w-40">
                                                    <BaseSelect
                                                        :label="$t('package.step3.durationLabel')"
                                                        :searchable="false"
                                                        :customSizeLabel="14"
                                                        compact
                                                        v-model="item.selectedDuration"
                                                        :options="getAvailableDurations(item.selectedActivity)"
                                                        :option-label="(option) => formatDuration(option)"
                                                        :reduce="(opt) => opt"
                                                        :placeholder="$t('package.step3.durationPlaceholder')"
                                                        :disabled="!item.selectedActivity"
                                                        class="transparent-select"
                                                        class-added="!ppv2-rounded-[4px] !ppv2-h-[30px]"
                                                        @input="onDurationChange(index, $event)"
                                                        :floatingLabel="true"
                                                        :floatingLabelBg="theme.mode === 'dark' ? '#131733' : '#f3f4f5'"
                                                    />
                                                </div>

                                                <!-- Options Toggles -->
                                                <div class="ppv2-flex ppv2-flex-col ppv2-justify-end">
                                                    <BaseSwitch
                                                        v-model="item.isRequired"
                                                        :label="$t('package.step3.toggles.required')"
                                                        :label-left="null"
                                                        customClass="!ppv2-mb-0 ppv2-h-[40px]"
                                                    />
                                                    <!-- <BaseSwitch
                                                        v-model="item.isHidden"
                                                    :label="$t('package.step3.toggles.hidden')"
                                                        :label-left="null"
                                                        customClass="!ppv2-mb-0"
                                                    /> -->
                                                </div>
                                            </div>
                                        </div>
                                        <!-- Remove Button (Outside) -->
                                        <div
                                            class="ppv2-bg-white ppv2-bg-opacity-5 border_btns ppv2-opacity-75 ppv2-text-black ppv2-text-opacity-90 d-flex align-items-center justify-center px-2"
                                            :style="{ width: '26px', marginLeft: normalizeSize(14), height: '26px' }"
                                        >
                                            <button
                                                type="button"
                                                @click="removeItem(index)"
                                                class="transparent-btn ppv2-text-black ppv2-flex ppv2-items-center ppv2-justify-center"
                                                title="Supprimer"
                                            >
                                                <Minus :size="15" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <!-- Add Item Button -->
                                <div
                                    v-if="comboItems.length < maxItems"
                                    class="ppv2-flex ppv2-justify-center"
                                    :style="{ marginBottom: normalizeSizeVertical(10) }"
                                >
                                    <BaseButton type="button" variant="secondary" @click="addNewItem" :customFontSize="12">
                                        <span class="ppv2-flex ppv2-items-center ppv2-gap-2">
                                            <Plus :size="12" />
                                            {{ $t('package.step3.addButton') }}
                                        </span>
                                    </BaseButton>
                                </div>
                            </div>
                        </template>
                    </DashedCard>
                </div>

                <!-- Même déroulé -->
                <div class="ppv2-mb-6" :style="{ marginTop: normalizeSizeVertical(30, true) }">
                    <SelectableList
                        :label="$t('package.step5.sameOrder.question')"
                        :value="parameters.is_order_required"
                        @input="(val) => updateParameterWithSideEffects({ key: 'is_order_required', value: val })"
                        :options="orderOptions"
                    />
                </div>
            </div>

            <!-- Validation Error -->
            <div v-if="errors.comboItems" class="error-text ppv2-mb-4">
                {{ errors.comboItems }}
            </div>

            <!-- Navigation Buttons -->
            <div class="form-actions" :class="{ '!ppv2-justify-end': !isCreationMode }">
                <BackButton v-if="isCreationMode" type="button" @click="$emit('previous-step')" />
                <BaseButton id="submit" type="button" @click="submitForm" variant="primary" :loading="loading">
                    {{ isCreationMode ? $t('experience.next') : $t('common.save') }}
                </BaseButton>
            </div>
        </div>

        <!-- Preview Panel -->
        <PreviewPackage :activeField="activeField" :step="step" />

        <!-- Confirmation Dialog -->
        <ConfirmationDialog
            :isOpen="showConfirmationDialog"
            :title="$t('package.step3.confirmChangeSameTimeTitle')"
            :message="$t('package.step3.confirmChangeSameTime')"
            type="warning"
            confirmButtonVariant="warning"
            :forceButtonsRow="true"
            @confirm="handleConfirmChange"
            @cancel="handleCancelChange"
        />
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import normalizeSize from '../../../../utils/normalizeSize'
import normalizeSizeVertical from '../../../../utils/normalizeSizeVertical'
import HeaderStep from './HeaderStep.vue'
import PreviewPackage from './PreviewPackage.vue'
import BaseButton from '../../../Shared/BaseButton.vue'
import BackButton from '../../../Shared/BackButton.vue'
import DashedCard from '../../../Shared/DashedCard.vue'
import BaseSwitch from '../../../Shared/BaseSwitch.vue'
import BaseSelect from '../../../Shared/BaseSelect.vue'
import SelectableList from '../../../Shared/SelectableList.vue'
import ConfirmationDialog from '../../../Shared/ConfirmationDialog.vue'
import { Plus, X, Minus, GripVertical } from 'lucide-vue'

export default {
    name: 'PackageStep3',
    components: {
        HeaderStep,
        PreviewPackage,
        BaseButton,
        BackButton,
        DashedCard,
        BaseSwitch,
        BaseSelect,
        SelectableList,
        Plus,
        Minus,
        GripVertical,
        GripVertical,
        X,
        ConfirmationDialog,
    },
    props: {
        step: { type: Object, required: true },
        sizes: { type: Object, required: true },
        id: { type: [Number, String], default: null },
        isCreationMode: { type: Boolean, default: false },
    },
    data() {
        return {
            activeField: null,
            errors: {},
            initialDataString: null,
            maxItems: 25,
            draggedIndex: null,
            sameTimeOptions: [
                {
                    label: 'Oui',
                    value: true,
                    subtitle: "Toutes les expériences ont le même créneau d'accès",
                },
                {
                    label: 'Non',
                    value: false,
                    subtitle: 'Chaque expérience a son propre créneau horaire',
                },
            ],
            showConfirmationDialog: false,
            pendingSameTimeValue: null,
            multiParticipantsOptions: [
                {
                    label: this.$t('package.step5.differentParticipants.yes') || 'Oui',
                    value: true,
                    subtitle:
                        this.$t('package.step5.differentParticipants.yesSubtitle') ||
                        'Chaque participant peut choisir une activité différente',
                },
                {
                    label: this.$t('package.step5.differentParticipants.no') || 'Non',
                    value: false,
                    subtitle:
                        this.$t('package.step5.differentParticipants.noSubtitle') ||
                        'Tous les participants font les mêmes activités',
                },
            ],
            orderOptions: [
                {
                    label: this.$t('package.step5.sameOrder.yes') || 'Oui',
                    value: true,
                    subtitle:
                        this.$t('package.step5.sameOrder.yesSubtitle') ||
                        "L'ordre de sélèction des éléments sera l'ordre d'execution",
                },
                {
                    label: this.$t('package.step5.sameOrder.no') || 'Non',
                    value: false,
                    subtitle: this.$t('package.step5.sameOrder.noSubtitle') || "Pas d'ordre spécifique requis",
                },
            ],
        }
    },
    computed: {
        ...mapGetters('package', ['comboItems', 'availableActivities', 'isEditMode', 'isStep3Valid', 'parameters', 'loading']),
        comboItems: {
            get() {
                return this.$store.getters['package/comboItems']
            },
            set(value) {
                this.$store.dispatch('package/setComboItems', value)
            },
        },
        selectedType() {
            return this.$store.getters['package/selectedType']
        },
        parameters: {
            get() {
                return this.$store.getters['package/parameters']
            },
            set(value) {
                this.$store.dispatch('package/setParameters', value)
            },
        },
        areDurationsSame() {
            const comboItems = this.comboItems
            if (!comboItems?.length) return true

            const firstDuration = comboItems[0]?.selectedDuration?.duration

            if (firstDuration === undefined) return true

            return comboItems.every((activity) => {
                const duration = activity.selectedDuration?.duration
                if (duration == null || duration === '') return true
                return duration === firstDuration
            })
        },
        shouldShowMultiParticipants() {
            return true
            if (!this.isEditMode) return true
            return this.selectedType === 'family'
        },
    },
    mounted() {
        // Add initial item if empty
        if (this.comboItems.length === 0) {
            this.addNewItem()
        }
        this.initialDataString = this.getFormDataString()
    },
    methods: {
        getFormDataString() {
            return JSON.stringify({
                comboItems: this.comboItems,
                parameters: { ...this.parameters },
            })
        },

        hasChanges() {
            return this.initialDataString !== this.getFormDataString()
        },

        resetInitialState() {
            this.initialDataString = this.getFormDataString()
        },
        ...mapActions('package', [
            'addComboItem',
            'updateComboItem',
            'removeComboItem',
            'savePackage',
            'setComboItems',
            'setParameters',
            'updateParameterWithSideEffects',
        ]),
        normalizeSize,
        normalizeSizeVertical,

        getAvailableActivities(currentIndex) {
            return this.$store.getters['package/getAvailableActivities'](currentIndex)
        },

        getAvailableDurations(activity) {
            if (!activity) return []

            const durations = activity.prices_by_times || activity.pricesByTimes || activity.durations || activity.prices || []

            // Filter logic for is_same_time
            if (this.parameters.is_same_time) {
                const firstDuration = this.comboItems?.[0]?.selectedDuration?.duration
                if (firstDuration) {
                    return durations.filter((d) => d.duration == firstDuration)
                }
            }

            return durations
        },

        formatDuration(option) {
            if (!option) return ''
            const { formatDuration: formatDurationUtil } = require('../../../../utils/duration')
            return formatDurationUtil(option.duration, this.$i18n)
        },

        formatDurationPrice(duration, price) {
            const { formatDuration: formatDurationUtil } = require('../../../../utils/duration')
            const formatted = formatDurationUtil(duration, this.$i18n)
            if (price) {
                return `${formatted} - ${price}${this.$symbolCurrency}`
            }
            return formatted
        },

        formatDurationOption(option) {
            if (!option) return ''
            return this.formatDurationPrice(option.duration, option.price)
        },

        addNewItem() {
            this.addComboItem({
                selectedActivity: null,
                selectedDuration: null,
                isRequired: false,
            })
        },

        removeItem(index) {
            this.removeComboItem(index)
        },

        onActivityChange(index, activity) {
            this.activeField = 'activities'
            // Update activity and reset duration
            this.updateComboItem({
                index,
                data: {
                    selectedActivity: activity,
                    selectedDuration: null,
                },
            })
        },

        onDurationChange(index, duration) {
            this.updateComboItem({
                index,
                data: { selectedDuration: duration },
            })
        },

        onSameTimeChange(value) {
            const hasData = this.comboItems.length > 0 && (this.comboItems.length > 1 || this.comboItems[0].selectedActivity)
            if (hasData && value) {
                this.pendingSameTimeValue = value
                this.showConfirmationDialog = true
            } else {
                this.updateParameterWithSideEffects({ key: 'is_same_time', value: value })
            }
        },

        handleConfirmChange() {
            this.updateParameterWithSideEffects({ key: 'is_same_time', value: this.pendingSameTimeValue })
            this.setComboItems([])
            this.addNewItem()
            this.showConfirmationDialog = false
            this.pendingSameTimeValue = null
        },

        handleCancelChange() {
            this.showConfirmationDialog = false
            this.pendingSameTimeValue = null
            // Force update to revert the SelectableList visual state if needed
            // Since SelectableList uses :value="parameters.is_same_time", it should revert automatically
            // because we didn't update the store.
        },

        validateForm() {
            this.errors = {}

            if (this.comboItems.length === 0) {
                this.errors.comboItems = this.$t('package.step3.errors.atLeastOne')
                return false
            }

            const hasIncomplete = this.comboItems.some((item) => !item.selectedActivity || !item.selectedDuration)

            if (hasIncomplete) {
                this.errors.comboItems = this.$t('package.step3.errors.incomplete')
                return false
            }

            if (this.parameters.is_same_time && !this.areDurationsSame) {
                this.errors.comboItems = this.$t('package.step3.errors.durationMismatch')
                return false
            }

            return true
        },

        async submitForm() {
            if (!this.validateForm()) {
                return
            }
            try {
                await this.savePackage()
                this.resetInitialState()
                if (!this.isCreationMode) {
                    this.$toast.success(this.$t('package.notifications.success_update'))
                }
                if (this.isCreationMode) {
                    this.$emit('next-step')
                }
            } catch (error) {
                this.$toast.error(error?.response?.data?.message || this.$t('package.notifications.saveErrorDefault'))
            }
        },

        async saveStep(redirect = false) {
            try {
                await this.savePackage()
                this.resetInitialState()
                if (redirect) {
                    const baseUrl = `${window.location.protocol}//${window.location.host}`
                    window.location.href = `${baseUrl}/back-office/v3/experience`
                }
            } catch (error) {
                this.$toast.error(error?.response?.data?.message || this.$t('package.notifications.saveErrorDefault'))
            }
        },

        onDragStart(evt, index) {
            evt.dataTransfer.setData('text/plain', index)
            evt.dataTransfer.effectAllowed = 'move'
            this.draggedIndex = index
        },

        onDragOver(evt) {
            evt.preventDefault()
            evt.dataTransfer.dropEffect = 'move'
        },

        onDrop(evt, targetIndex) {
            evt.preventDefault()
            const fromIndex = this.draggedIndex
            const toIndex = targetIndex

            // Remove background color style
            const container = evt.currentTarget.closest('.combo-item-container')
            if (container) container.style.backgroundColor = ''

            if (fromIndex !== toIndex && fromIndex !== null) {
                // Move item in the array
                const items = [...this.comboItems]
                const item = items.splice(fromIndex, 1)[0]
                items.splice(toIndex, 0, item)
                this.comboItems = items
            }
            this.draggedIndex = null
        },

        onDragEnter(evt) {
            const container = evt.currentTarget.closest('.combo-item-container')
            if (container) container.style.backgroundColor = 'rgba(255, 255, 255, 0.05)'
        },

        onDragLeave(evt) {
            const container = evt.currentTarget.closest('.combo-item-container')
            if (container && !container.contains(evt.relatedTarget)) {
                container.style.backgroundColor = ''
            }
        },

        onDragEnd(evt) {
            this.draggedIndex = null
            // Clean up any remaining styles
            const allItems = document.querySelectorAll('.combo-item-container')
            allItems.forEach((item) => {
                item.style.backgroundColor = ''
            })
        },
    },
}
</script>

<style scoped>
.form-section {
    flex: 1;
}

.form-actions {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    margin-top: 40px;
}

.combo-item {
    position: relative;
    border-radius: 12px;
    border: 1px solid #545595;
    background-color: #1a1f370d;
    padding: 10px 13px 16px;
    transition: all 0.2s ease;
}

.dark .combo-item {
    background-color: rgba(255, 255, 255, 0.05);
}

.custom-v-select :deep(.vs__dropdown-toggle) {
    background: rgba(30, 41, 59, 0.5);
    border-color: rgba(71, 85, 105, 0.5);
    border-radius: 8px;
    padding: 8px 12px;
    min-height: 44px;
}

.custom-v-select :deep(.vs__search) {
    color: white;
}

.custom-v-select :deep(.vs__selected) {
    color: white;
}

.custom-v-select :deep(.vs__dropdown-menu) {
    background: rgb(30, 41, 59);
    border-color: rgba(71, 85, 105, 0.5);
}

.custom-v-select :deep(.vs__dropdown-option) {
    color: white;
}

.custom-v-select :deep(.vs__dropdown-option--highlight) {
    background: rgba(125, 45, 189, 0.3);
}

.border_btns {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    border-radius: 7px;
    border: 1px solid #545595;
    min-height: 26px;
    overflow: hidden;
}

.transparent-btn {
    width: 100% !important;
    height: 100% !important;
    box-shadow: none !important;
    position: absolute;
    left: 0;
    top: 0;
    border: none;
    min-width: auto !important;
    background: transparent;
}

.transparent-btn:hover {
    background: #d9d9d91a !important;
}

.ppv2-cursor-grab {
    cursor: grab;
}

.ppv2-cursor-grab:active {
    cursor: grabbing;
}

.combo-item-container {
    transition: background-color 0.2s ease;
    border-radius: 12px;
}
</style>
